import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingBag, Search, Filter, ChevronDown, Grid3X3, LayoutList,
  ShoppingCart, Plus, Minus, X, Loader2, Package, Tag, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import PageHeader from '../components/PageHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

const CATEGORY_OPTIONS = ['All', 'Clothing', 'Accessories', 'Dance Shoes', 'Music', 'Gifts', 'Other'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'popular', label: 'Most Popular' }
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Shop settings
  const [shopSettings, setShopSettings] = useState({
    shop_name: 'Love Line Dancing',
    tagline: 'Quality merchandise for line dance enthusiasts',
    logo_url: null
  });
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  
  // Cart state
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  
  // Product detail modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchShopSettings();
    loadCart();
  }, []);

  useEffect(() => {
    fetchProducts();
    // Update URL params
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category && category !== 'All') params.set('category', category);
    if (sort && sort !== 'newest') params.set('sort', sort);
    setSearchParams(params);
  }, [search, category, sort]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category && category !== 'All') params.set('category', category);
      if (sort) params.set('sort', sort);
      params.set('limit', '50');
      
      const res = await fetch(`${API_URL}/api/shop/products?${params}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchShopSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/settings`);
      if (res.ok) {
        const data = await res.json();
        setShopSettings(data);
      }
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    }
  };

  const loadCart = async () => {
    // Check localStorage for existing cart
    const cartId = localStorage.getItem('shop_cart_id');
    if (cartId) {
      try {
        const res = await fetch(`${API_URL}/api/shop/cart/${cartId}`);
        if (res.ok) {
          const data = await res.json();
          setCart(data);
        } else {
          // Cart expired or not found, clear it
          localStorage.removeItem('shop_cart_id');
        }
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    }
  };

  const createCart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('shop_cart_id', data.cart_id);
        setCart(data);
        return data;
      }
    } catch (error) {
      console.error('Error creating cart:', error);
    }
    return null;
  };

  const addToCart = async () => {
    if (!selectedProduct) return;
    
    // Validate variant selection if product has variants
    if (selectedProduct.has_variants && selectedProduct.variants?.length > 0 && !selectedVariant) {
      toast.error('Please select a size/color');
      return;
    }
    
    setAddingToCart(true);
    
    try {
      let currentCart = cart;
      if (!currentCart) {
        currentCart = await createCart();
        if (!currentCart) {
          toast.error('Failed to create cart');
          return;
        }
      }
      
      const res = await fetch(`${API_URL}/api/shop/cart/${currentCart.cart_id}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: selectedProduct.product_id,
          variant_id: selectedVariant?.variant_id || null,
          quantity: quantity
        })
      });
      
      if (res.ok) {
        const updatedCart = await res.json();
        setCart(updatedCart);
        toast.success('Added to cart!');
        setSelectedProduct(null);
        setSelectedVariant(null);
        setQuantity(1);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const updateCartItem = async (productId, variantId, newQuantity) => {
    if (!cart) return;
    
    setCartLoading(true);
    try {
      if (newQuantity <= 0) {
        // Remove item
        const res = await fetch(
          `${API_URL}/api/shop/cart/${cart.cart_id}/items/${productId}${variantId ? `?variant_id=${variantId}` : ''}`,
          { method: 'DELETE' }
        );
        if (res.ok) {
          await loadCart();
        }
      } else {
        // Update quantity
        const res = await fetch(`${API_URL}/api/shop/cart/${cart.cart_id}/items/${productId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQuantity, variant_id: variantId })
        });
        if (res.ok) {
          await loadCart();
        }
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Failed to update cart');
    } finally {
      setCartLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const openProductDetail = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(null);
    setSelectedImageIndex(0);
    setQuantity(1);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Hero Section (shared PageHeader) */}
      <PageHeader
        align="center"
        title={
          <span className="flex flex-col items-center gap-3">
            {shopSettings.logo_url && (
              <img
                src={shopSettings.logo_url.startsWith('/') ? `${API_URL}${shopSettings.logo_url}` : shopSettings.logo_url}
                alt={shopSettings.shop_name}
                className="h-16 md:h-24 mx-auto object-contain"
              />
            )}
            <span>{shopSettings.shop_name}</span>
          </span>
        }
        subtitle={shopSettings.tagline}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white"
              data-testid="shop-search"
            />
          </div>
          
          {/* Category */}
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full md:w-[180px] bg-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Sort */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full md:w-[180px] bg-white">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View Toggle */}
          <div className="flex gap-1 bg-white rounded-lg p-1 border">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Cart Button */}
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="relative bg-white" data-testid="cart-btn">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Cart
                {cartItemCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-violet-600">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg">
              <SheetHeader>
                <SheetTitle>Shopping Cart</SheetTitle>
                <SheetDescription>
                  {cartItemCount} item{cartItemCount !== 1 ? 's' : ''} in your cart
                </SheetDescription>
              </SheetHeader>
              
              <div className="mt-6 flex-1 overflow-y-auto">
                {!cart?.items?.length ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.items.map((item, idx) => (
                      <div key={idx} className="flex gap-4 bg-slate-50 p-3 rounded-lg">
                        {item.image ? (
                          <img
                            src={item.image.startsWith('/') ? `${API_URL}${item.image}` : item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center">
                            <Package className="h-6 w-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-violet-600 font-semibold">{formatCurrency(item.price)}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateCartItem(item.product_id, item.variant_id, item.quantity - 1)}
                              disabled={cartLoading}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateCartItem(item.product_id, item.variant_id, item.quantity + 1)}
                              disabled={cartLoading}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 ml-auto text-red-500"
                              onClick={() => updateCartItem(item.product_id, item.variant_id, 0)}
                              disabled={cartLoading}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {cart?.items?.length > 0 && (
                <SheetFooter className="mt-6 border-t pt-4">
                  <div className="w-full space-y-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Subtotal</span>
                      <span>{formatCurrency(cart.subtotal)}</span>
                    </div>
                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      size="lg"
                      onClick={() => {
                        setCartOpen(false);
                        // Navigate to checkout
                        window.location.href = `/shop/checkout?cart=${cart.cart_id}`;
                      }}
                      data-testid="checkout-btn"
                    >
                      Proceed to Checkout
                    </Button>
                  </div>
                </SheetFooter>
              )}
            </SheetContent>
          </Sheet>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No products found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
            }
          >
            {products.map((product) => (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <Card 
                  className={`group cursor-pointer overflow-hidden bg-white hover:shadow-lg transition-all ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                  onClick={() => openProductDetail(product)}
                  data-testid={`product-card-${product.product_id}`}
                >
                  {/* Product Image */}
                  <div className={`relative ${viewMode === 'list' ? 'w-40 flex-shrink-0' : 'aspect-square'} overflow-hidden`}>
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0].startsWith('/') ? `${API_URL}${product.images[0]}` : product.images[0]}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                        <Package className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    {product.compare_at_price && product.compare_at_price > product.price && (
                      <Badge className="absolute top-2 left-2 bg-red-500">
                        Sale
                      </Badge>
                    )}
                    {product.has_variants && (
                      <Badge variant="secondary" className="absolute top-2 right-2">
                        {product.variants?.length} options
                      </Badge>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <CardContent className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    {product.category && (
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                        {product.category}
                      </p>
                    )}
                    <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-violet-600 transition-colors">
                      {product.title}
                    </h3>
                    {viewMode === 'list' && product.description && (
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2 whitespace-pre-line">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-violet-600">
                        {formatCurrency(product.price)}
                      </span>
                      {product.compare_at_price && product.compare_at_price > product.price && (
                        <span className="text-sm text-slate-400 line-through">
                          {formatCurrency(product.compare_at_price)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => { if (!open) { setSelectedProduct(null); setSelectedImageIndex(0); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedProduct && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Images */}
              <div className="space-y-3">
                <div className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                  {selectedProduct.images?.[selectedImageIndex] ? (
                    <img
                      src={selectedProduct.images[selectedImageIndex].startsWith('/') ? `${API_URL}${selectedProduct.images[selectedImageIndex]}` : selectedProduct.images[selectedImageIndex]}
                      alt={selectedProduct.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-16 w-16 text-slate-300" />
                    </div>
                  )}
                </div>
                {selectedProduct.images?.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto">
                    {selectedProduct.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.startsWith('/') ? `${API_URL}${img}` : img}
                        alt={`${selectedProduct.title} ${idx + 1}`}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`w-16 h-16 object-cover rounded border-2 cursor-pointer transition-all ${
                          idx === selectedImageIndex 
                            ? 'border-violet-500 ring-2 ring-violet-200' 
                            : 'border-transparent hover:border-violet-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Details */}
              <div className="space-y-4">
                <DialogHeader>
                  <DialogTitle className="text-2xl">{selectedProduct.title}</DialogTitle>
                  {selectedProduct.category && (
                    <Badge variant="secondary">{selectedProduct.category}</Badge>
                  )}
                </DialogHeader>
                
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-violet-600">
                    {formatCurrency(selectedVariant?.price || selectedProduct.price)}
                  </span>
                  {(selectedProduct.compare_at_price || selectedVariant?.compare_at_price) && (
                    <span className="text-lg text-slate-400 line-through">
                      {formatCurrency(selectedVariant?.compare_at_price || selectedProduct.compare_at_price)}
                    </span>
                  )}
                </div>
                
                {selectedProduct.description && (
                  <p className="text-slate-600 whitespace-pre-line">{selectedProduct.description}</p>
                )}
                
                {/* Variant Selection */}
                {selectedProduct.has_variants && selectedProduct.variants?.length > 0 && (
                  <div className="space-y-3">
                    <p className="font-medium text-slate-700">
                      Select Option <span className="text-red-500">*</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.variants.map((variant) => {
                        const isDiscontinued = variant.is_discontinued;
                        const isOutOfStock = variant.track_inventory && variant.stock_quantity <= 0;
                        const canBackorder = selectedProduct.allow_backorder !== false;
                        // Only show as unavailable if discontinued OR (out of stock AND no backorder allowed)
                        const isUnavailable = isDiscontinued || (isOutOfStock && !canBackorder);
                        
                        return (
                          <Button
                            key={variant.variant_id}
                            variant={selectedVariant?.variant_id === variant.variant_id ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedVariant(variant)}
                            disabled={isUnavailable}
                            className={isDiscontinued ? 'opacity-50 line-through' : (isOutOfStock && !canBackorder) ? 'opacity-50' : ''}
                          >
                            {variant.name}
                            {isDiscontinued && ' (Discontinued)'}
                            {!isDiscontinued && isOutOfStock && !canBackorder && ' (Out of stock)'}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Quantity */}
                <div className="space-y-2">
                  <p className="font-medium text-slate-700">Quantity</p>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center font-medium">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Add to Cart */}
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  size="lg"
                  onClick={addToCart}
                  disabled={
                    addingToCart || 
                    (selectedProduct.has_variants && !selectedVariant) ||
                    selectedProduct.is_discontinued ||
                    (selectedVariant && selectedVariant.is_discontinued)
                  }
                  data-testid="add-to-cart-btn"
                >
                  {addingToCart ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ShoppingCart className="h-4 w-4 mr-2" />
                  )}
                  {selectedProduct.is_discontinued || selectedVariant?.is_discontinued 
                    ? 'Discontinued' 
                    : 'Add to Cart'}
                </Button>
                
                {/* Stock info - only show discontinued or truly out of stock (no backorder) */}
                {selectedProduct.is_discontinued ? (
                  <p className="text-sm text-red-600 font-medium">
                    This product has been discontinued
                  </p>
                ) : selectedProduct.track_inventory && !selectedProduct.has_variants && selectedProduct.stock_quantity <= 0 && selectedProduct.allow_backorder === false ? (
                  <p className="text-sm text-red-600">Out of stock</p>
                ) : null}
                {selectedVariant?.is_discontinued ? (
                  <p className="text-sm text-red-600 font-medium">
                    This variant has been discontinued
                  </p>
                ) : selectedVariant?.track_inventory && selectedVariant.stock_quantity <= 0 && selectedProduct.allow_backorder === false ? (
                  <p className="text-sm text-red-600">Out of stock</p>
                ) : null}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
